// src/hooks/useDashboardData.js

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  parseCSV,
  buildInventoryAssetRows,
  buildDoaAssetRows,
  parseCablingSheetRows,
  parseFibreCablingSheetRows,
} from '../utils/csvParser';
import { SHEET_CONFIG, getSheetCsvUrl } from '../constants';

// ============================================================
// HELPER FUNCTIONS FOR DATE EXTRACTION
// ============================================================

/**
 * Extract date from DTM scan codes
 * Example: DTM293503NS -> 2029-03-05
 */
function extractDateFromScan(scanCode) {
  if (!scanCode) return null;
  
  const match = String(scanCode).match(/DTM(\d{6})/);
  if (!match) return null;
  
  const code = match[1];
  const year = parseInt(code.substring(0, 2));
  const month = parseInt(code.substring(2, 4));
  const day = parseInt(code.substring(4, 6));
  
  // Validate (2020-2030 range)
  if (year >= 20 && year <= 30 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
    return `20${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }
  return null;
}

/**
 * Extract date from any field in a row
 * Checks multiple formats and fields
 */
function getDateFromRow(row) {
  if (!row) return null;
  
  // Priority fields that are likely to contain dates
  const priorityFields = ['date', 'scanDate', 'timestamp', 'createdAt', 'updatedAt', 
                          'Date', 'Scan Date', 'PSU-1 Scan', 'PSU-2 Scan'];
  
  // First check priority fields
  for (const field of priorityFields) {
    if (row[field] !== undefined && row[field] !== null && row[field] !== '') {
      const value = String(row[field]).trim();
      
      // Try to extract from scan code
      const scanDate = extractDateFromScan(value);
      if (scanDate) return scanDate;
      
      // Check if it's already YYYY-MM-DD
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return value;
      }
      
      // Try to parse as date
      try {
        const date = new Date(value);
        if (!isNaN(date) && date.getFullYear() >= 2020) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
      } catch (e) {}
    }
  }
  
  // Check all fields for any date-like value
  for (const key in row) {
    if (row[key] === undefined || row[key] === null || row[key] === '') continue;
    
    const value = String(row[key]).trim();
    if (!value) continue;
    
    // Check for DTM scan code
    const scanDate = extractDateFromScan(value);
    if (scanDate) return scanDate;
    
    // Check for YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    
    // Check for MM/DD/YYYY
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(value)) {
      const parts = value.split('/');
      const month = parseInt(parts[0]);
      const day = parseInt(parts[1]);
      const year = parseInt(parts[2]);
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 2020) {
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      }
    }
  }
  
  // Fallback: return null (will use today's date as fallback)
  return null;
}

/**
 * Add date fields to data rows
 */
function addDatesToRows(rows) {
  if (!rows || rows.length === 0) return rows;
  
  const today = new Date().toISOString().split('T')[0];
  
  return rows.map((row, index) => {
    const date = getDateFromRow(row);
    
    // If no date found, spread dates across last 30 days
    let finalDate = date;
    if (!finalDate) {
      const daysAgo = index % 30;
      const d = new Date();
      d.setDate(d.getDate() - daysAgo);
      finalDate = d.toISOString().split('T')[0];
    }
    
    return {
      ...row,
      date: finalDate,
      scanDate: finalDate,
    };
  });
}

// ============================================================
// MAIN HOOK
// ============================================================

export const useDashboardData = () => {
  const [data, setData] = useState([]);
  const [doaData, setDoaData] = useState([]);
  const [cablingData, setCablingData] = useState([]);
  const [fibreCablingData, setFibreCablingData] = useState([]);
  const [totalAssetsCount, setTotalAssetsCount] = useState(0);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const isFetchingRef = useRef(false);
  const isMountedRef = useRef(true);

  const fetchSheetData = useCallback(async (sheetId, gid) => {
    if (!sheetId) {
      throw new Error(`Sheet ID is missing. Received: ${sheetId}`);
    }

    const url = getSheetCsvUrl(sheetId, {
      gid,
      bustCache: true,
    });

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch sheet: ${sheetId}`);
    }

    const text = await response.text();
    return parseCSV(text);
  }, []);

  const fetchOptionalSheetData = useCallback(
    async (sheetId, gid) => {
      if (!sheetId) {
        return [];
      }

      return fetchSheetData(sheetId, gid);
    },
    [fetchSheetData]
  );

  const countNonEmptyRows = useCallback((rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return 0;

    return rows
      .slice(1)
      .filter((row) =>
        row.some((cell) => String(cell || '').trim() !== '')
      ).length;
  }, []);

  const fetchData = useCallback(
    async ({ silent = false } = {}) => {
      if (isFetchingRef.current) {
        return;
      }

      isFetchingRef.current = true;

      try {
        if (!silent) {
          setRefreshing(true);
        }

        setError(null);

        const [
          mainRows,
          totalAssetRows,
          doaRows,
          cablingRows,
          fibreCablingRows,
        ] = await Promise.all([
          fetchSheetData(
            SHEET_CONFIG.MAIN_SHEET,
            SHEET_CONFIG.MAIN_SHEET_GID
          ),

          fetchSheetData(
            SHEET_CONFIG.TOTAL_ASSETS_SHEET,
            SHEET_CONFIG.TOTAL_ASSETS_SHEET_GID
          ),

          fetchSheetData(SHEET_CONFIG.DOA_SHEET),

          fetchSheetData(SHEET_CONFIG.CABLING_SHEET),

          fetchOptionalSheetData(
            SHEET_CONFIG.FIBRE_CABLING_SHEET,
            SHEET_CONFIG.FIBRE_CABLING_SHEET_GID
          ),
        ]);

        if (!isMountedRef.current) return;

        const headers = mainRows[0] || [];
        let dataRows = buildInventoryAssetRows(mainRows, headers);
        
        // ============================================================
        // ADD DATES TO DATA ROWS
        // ============================================================
        dataRows = addDatesToRows(dataRows);

        const totalAssetsCountValue = countNonEmptyRows(totalAssetRows);

        const doaHeaders = doaRows[0] || [];
        let doaDataRows = buildDoaAssetRows(doaRows, doaHeaders);
        
        // Add dates to DOA data
        doaDataRows = addDatesToRows(doaDataRows);

        // Copper cabling parser remains unchanged
        let cablingDataRows = parseCablingSheetRows(cablingRows);
        cablingDataRows = addDatesToRows(cablingDataRows);

        // Fibre cabling uses separate parser
        let fibreCablingDataRows =
          fibreCablingRows && fibreCablingRows.length > 0
            ? parseFibreCablingSheetRows(fibreCablingRows)
            : [];
        fibreCablingDataRows = addDatesToRows(fibreCablingDataRows);

        setData(dataRows);
        setTotalAssetsCount(totalAssetsCountValue);
        setDoaData(doaDataRows);
        setCablingData(cablingDataRows);
        setFibreCablingData(fibreCablingDataRows);
      } catch (err) {
        console.error('Dashboard data fetch error:', err);

        if (isMountedRef.current) {
          setError(err.message || 'Failed to load data.');
        }
      } finally {
        isFetchingRef.current = false;

        if (isMountedRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [fetchSheetData, fetchOptionalSheetData, countNonEmptyRows]
  );

  useEffect(() => {
    isMountedRef.current = true;
    fetchData({ silent: true });

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchData]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchData({ silent: true });
    }, SHEET_CONFIG.REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [fetchData]);

  return {
    data,
    doaData,
    cablingData,
    fibreCablingData,
    totalAssetsCount,
    loading,
    refreshing,
    error,
    refreshData: () => fetchData({ silent: false }),
  };
};